// ActivityComposer.jsx

import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { toast } from 'react-toastify';

// Context
import { useTheme } from 'context/ThemeContext';
import { useUser } from 'context/UserContext';

// Utilities
import {
  ADD_MODAL_MEDIA_ACTION_KEYS,
} from 'components/addmodal/utilities/mediaActions';
import {
  COMMENT_ATTACHMENT_ORIGINS,
  buildCommentAttachmentFromNode,
  buildLocalDraftAttachment,
  revokeDraftPreviewUrl,
  sanitizeCommentAttachmentsForWrite,
} from 'node/activity/utils/commentAttachments';

// Services
import { uploadService } from 'services/nodes/NodeUploadService';
import { moveNodesToTrash } from 'services/nodes/NodeWriteService';

// CSS
import './ActivityComposer.css';

// Components
import ActivityCommentAttachments from 'node/activity/items/ActivityCommentAttachments';
import AddModal from 'components/addmodal/AddModal';
import CameraModal from 'editor/nodeadd/camera/CameraModal';
import NodeHiddenFileInputs from 'node/inputs/NodeHiddenFileInputs';
import TextInput from 'components/textinput/TextInput';
import Icon from 'svg/Icon';

/**
 * Convert a camera capture payload into an uploadable File-like object.
 *
 * @param {object|null|undefined} contributionNode
 * @returns {File|object|null}
 */
function nodeToUploadFile(contributionNode) {
  if (!contributionNode) return null;

  const fallbackType = contributionNode.mimeType
    || (contributionNode.type === 'PHOTO' ? 'image/jpeg'
      : contributionNode.type === 'VIDEO' ? 'video/webm'
        : undefined);
  const fallbackName = contributionNode.fileName
    || `${(contributionNode.type || 'capture').toLowerCase()}-${contributionNode.key || Date.now()}`;
  const baseFile = contributionNode.tempFile || contributionNode.localFile;
  const existingUri = contributionNode.localUri || contributionNode.url;
  const fileUri = existingUri
    || (baseFile && typeof URL !== 'undefined' ? URL.createObjectURL(baseFile) : null);

  if (baseFile instanceof File) {
    return Object.assign(baseFile, {
      uri: fileUri,
      localUri: fileUri,
      base64Data: contributionNode.base64Data || null,
    });
  }

  if (baseFile) {
    return {
      name: (typeof baseFile.name === 'string' && baseFile.name.length > 0)
        ? baseFile.name
        : fallbackName,
      type: (typeof baseFile.type === 'string' && baseFile.type.length > 0)
        ? baseFile.type
        : fallbackType,
      size: baseFile.size,
      lastModified: baseFile.lastModified,
      uri: fileUri,
      localUri: fileUri,
      base64Data: contributionNode.base64Data || null,
      localFile: baseFile,
    };
  }

  if (!fileUri) return null;

  return {
    name: fallbackName,
    type: fallbackType || 'application/octet-stream',
    uri: fileUri,
    localUri: fileUri,
    base64Data: contributionNode.base64Data || null,
  };
}

/**
 * Upload pending local files as related contributions; resolve with nodes.
 *
 * @param {object[]} pendingDrafts
 * @param {object} node
 * @param {object} user
 * @returns {Promise<object[]>}
 */
function uploadPendingDraftFiles(pendingDrafts, node, user) {
  const files = pendingDrafts.map((draft) => draft.file).filter(Boolean);
  if (files.length === 0) return Promise.resolve([]);

  return new Promise((resolve, reject) => {
    const uploadId = uploadService.queueContribution(
      files,
      node,
      user,
      (uploadedItems) => {
        resolve(Array.isArray(uploadedItems) ? uploadedItems : []);
      },
      (error) => {
        reject(error instanceof Error ? error : new Error(String(error || 'Upload failed')));
      }
    );

    if (uploadId === null) {
      reject(new Error('An upload is already in progress for this record. Try again in a moment.'));
    }
  });
}

/**
 * ActivityComposer
 *
 * Compact bottom comment composer for the Activity feed (SMS-style attachments:
 * local drafts until Send, then upload + post comment with chips).
 *
 * @param {object|null} node - Anchor node for related uploads / links.
 * @param {boolean} canComment - Whether the user may post.
 * @param {object|null} [replyingTo] - Optional reply target comment.
 * @param {function} onSubmit - async (text, attachments) => boolean
 * @param {function} [onCancelReply]
 * @param {function} [onDraftAttachmentKeysChange] - (string[]) => void
 * @returns {ReactNode}
 */
const ActivityComposer = React.memo(({
  node = null,
  canComment = false,
  replyingTo = null,
  onSubmit,
  onCancelReply,
  onDraftAttachmentKeysChange,
}) => {
  const { theme } = useTheme();
  const { user } = useUser();

  const [text, setText] = useState('');
  const [focused, setFocused] = useState(false);
  const [sending, setSending] = useState(false);
  const [draftAttachments, setDraftAttachments] = useState([]);
  const [addModalOpen, setAddModalOpen] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const [cameraContentType, setCameraContentType] = useState(null);

  const inputRef = useRef(null);
  const filesInputRef = useRef(null);
  const photoInputRef = useRef(null);
  const videoInputRef = useRef(null);
  const documentInputRef = useRef(null);
  const draftAttachmentsRef = useRef(draftAttachments);

  useEffect(() => {
    draftAttachmentsRef.current = draftAttachments;
  }, [draftAttachments]);

  // Only real nodeKeys suppress relationship system events (link-existing +
  // post-upload). Local file drafts have no node yet — nothing to suppress.
  const draftNodeKeys = useMemo(
    () => draftAttachments.map((entry) => entry.nodeKey).filter(Boolean),
    [draftAttachments]
  );

  useEffect(() => {
    onDraftAttachmentKeysChange?.(draftNodeKeys);
  }, [draftNodeKeys, onDraftAttachmentKeysChange]);

  useEffect(() => {
    if (!replyingTo) return;
    setFocused(true);
    window.requestAnimationFrame(() => {
      inputRef.current?.focus?.();
    });
  }, [replyingTo]);

  // Cleanup: revoke blob previews; trash upload/camera nodes that were created
  // on a failed/partial send but never posted on a comment.
  useEffect(() => () => {
    const drafts = draftAttachmentsRef.current || [];
    drafts.forEach((entry) => revokeDraftPreviewUrl(entry));

    const toTrash = drafts
      .filter((entry) => (
        entry?.nodeKey
        && (
          entry.origin === COMMENT_ATTACHMENT_ORIGINS.UPLOAD
          || entry.origin === COMMENT_ATTACHMENT_ORIGINS.CAMERA
        )
      ))
      .map((entry) => entry.nodeKey);

    if (toTrash.length === 0) return;
    void moveNodesToTrash(toTrash).catch((error) => {
      console.warn('[ActivityComposer] failed to trash draft uploads on unmount:', error);
    });
  }, []);

  const appendLocalFiles = useCallback((files, origin) => {
    const list = Array.isArray(files) ? files : [];
    if (list.length === 0) return;

    const next = list
      .map((file) => buildLocalDraftAttachment(file, origin))
      .filter(Boolean);

    if (next.length === 0) return;

    setDraftAttachments((prev) => [...prev, ...next]);
    setFocused(true);
  }, []);

  const handleFilesFromInput = useCallback((event) => {
    const files = Array.from(event?.target?.files || []);
    if (files.length > 0) {
      const filesWithUri = files.map((file) => {
        if (file.uri || file.localUri) return file;
        const uri = typeof URL !== 'undefined' ? URL.createObjectURL(file) : null;
        return Object.assign(file, { uri, localUri: uri });
      });
      appendLocalFiles(filesWithUri, COMMENT_ATTACHMENT_ORIGINS.UPLOAD);
    }
    if (event?.target) event.target.value = null;
  }, [appendLocalFiles]);

  const handleCameraContentCreated = useCallback((capturedContent) => {
    const file = nodeToUploadFile(capturedContent);
    setCameraOpen(false);
    setCameraContentType(null);
    if (!file) {
      toast.error('Camera capture did not produce a file.');
      return;
    }
    appendLocalFiles([file], COMMENT_ATTACHMENT_ORIGINS.CAMERA);
  }, [appendLocalFiles]);

  const handleQuickActionSelect = useCallback((typeKey) => {
    const normalized = String(typeKey || '').trim().toUpperCase();
    setAddModalOpen(false);

    if (
      normalized === ADD_MODAL_MEDIA_ACTION_KEYS.CAMERA
      || normalized === 'SNAPSHOT'
      || normalized === 'RECORDING'
      || normalized === 'PHOTO'
    ) {
      setCameraContentType(normalized === 'RECORDING' ? 'RECORDING' : 'SNAPSHOT');
      setCameraOpen(true);
      return;
    }

    if (
      normalized === ADD_MODAL_MEDIA_ACTION_KEYS.FILES
      || normalized === 'FILE'
      || normalized === 'DOCUMENT'
      || normalized === 'VIDEO'
      || normalized === 'ATTACH'
    ) {
      filesInputRef.current?.click();
    }
  }, []);

  const handleExistingAttached = useCallback((linkedNode) => {
    const attachment = buildCommentAttachmentFromNode(
      linkedNode,
      COMMENT_ATTACHMENT_ORIGINS.LINK,
      { draftId: linkedNode?.key || null }
    );
    if (!attachment) return;

    setDraftAttachments((prev) => {
      if (prev.some((row) => row.nodeKey && row.nodeKey === attachment.nodeKey)) {
        return prev;
      }
      return [...prev, attachment];
    });
    setFocused(true);
  }, []);

  const handleRemoveDraftAttachment = useCallback(async (draftIdOrNodeKey) => {
    const key = String(draftIdOrNodeKey || '').trim();
    if (!key) return;

    const existing = draftAttachmentsRef.current.find((row) => (
      row.draftId === key || row.nodeKey === key
    ));
    if (!existing) return;

    setDraftAttachments((prev) => prev.filter((row) => (
      row.draftId !== key && row.nodeKey !== key
    )));

    revokeDraftPreviewUrl(existing);

    // Only trash nodes that were already created for this draft (failed send /
    // link is never trashed). Local-only files were never uploaded.
    if (
      existing.nodeKey
      && (
        existing.origin === COMMENT_ATTACHMENT_ORIGINS.UPLOAD
        || existing.origin === COMMENT_ATTACHMENT_ORIGINS.CAMERA
      )
    ) {
      try {
        await moveNodesToTrash([existing.nodeKey]);
      } catch (error) {
        console.warn('[ActivityComposer] failed to trash removed draft upload:', error);
      }
    }
  }, []);

  const handleSubmit = useCallback(async () => {
    const body = String(text || '').trim();
    const drafts = draftAttachmentsRef.current || [];
    if ((!body && drafts.length === 0) || sending || !canComment) return;
    if (!node?.key || !user?.key) return;

    setSending(true);
    try {
      const pending = drafts.filter((entry) => entry.file && !entry.nodeKey);
      const alreadyReady = drafts.filter((entry) => entry.nodeKey);

      let uploadedAttachments = [];
      if (pending.length > 0) {
        const uploadedItems = await uploadPendingDraftFiles(pending, node, user);
        uploadedAttachments = pending.map((draft, index) => {
          const item = uploadedItems[index];
          if (!item?.key) return null;
          return buildCommentAttachmentFromNode(item, draft.origin, {
            draftId: draft.draftId,
          });
        }).filter(Boolean);

        // Suppress "added a photo" lines as soon as nodes exist, before comment write.
        const suppressKeys = [
          ...alreadyReady.map((row) => row.nodeKey).filter(Boolean),
          ...uploadedAttachments.map((row) => row.nodeKey).filter(Boolean),
        ];
        onDraftAttachmentKeysChange?.(suppressKeys);

        // Keep drafts as uploaded nodes so a comment-write failure can retry.
        setDraftAttachments((prev) => prev.map((row) => {
          if (!row.file || row.nodeKey) return row;
          const match = uploadedAttachments.find((entry) => entry.draftId === row.draftId);
          if (!match) return row;
          revokeDraftPreviewUrl(row);
          return {
            ...match,
            file: null,
            localPreview: false,
          };
        }));
      }

      const attachments = sanitizeCommentAttachmentsForWrite([
        ...alreadyReady,
        ...uploadedAttachments,
      ]);

      if (!body && attachments.length === 0) {
        toast.error('Could not attach files. Try again.');
        return;
      }

      const ok = await onSubmit?.(body, attachments);
      if (ok !== false) {
        (draftAttachmentsRef.current || []).forEach((entry) => revokeDraftPreviewUrl(entry));
        setText('');
        setFocused(false);
        setDraftAttachments([]);
      }
    } catch (error) {
      console.error('[ActivityComposer] send failed:', error);
      toast.error(error?.message || 'Could not send comment.');
    } finally {
      setSending(false);
    }
  }, [
    canComment,
    node,
    onDraftAttachmentKeysChange,
    onSubmit,
    sending,
    text,
    user,
  ]);

  const handleCancelReply = useCallback(() => {
    onCancelReply?.();
  }, [onCancelReply]);

  const handleKeyDown = useCallback((event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      handleSubmit();
    }
  }, [handleSubmit]);

  if (!canComment) {
    return (
      <div className="activity-composer-container is-disabled">
        Comments disabled
      </div>
    );
  }

  const replyName = String(replyingTo?.user?.name || replyingTo?.name || '').trim();
  const hasBody = Boolean(String(text || '').trim()) || draftAttachments.length > 0;
  const showSend = focused || hasBody || Boolean(replyingTo);
  const sendDisabled = sending || !hasBody;

  return (
    <div className="activity-composer-container">
      {replyingTo ? (
        <div className="activity-composer-reply-bar">
          <span className="activity-composer-reply-label">
            Replying to {replyName || 'comment'}
          </span>
          <button
            className="activity-composer-reply-cancel"
            type="button"
            onClick={handleCancelReply}
          >
            Cancel
          </button>
        </div>
      ) : null}

      {draftAttachments.length > 0 ? (
        <ActivityCommentAttachments
          attachments={draftAttachments}
          removable
          onRemove={handleRemoveDraftAttachment}
        />
      ) : null}

      <div className="activity-composer-row">
        <button
          className="activity-composer-attach"
          type="button"
          disabled={sending || !node?.key}
          aria-label="Add attachment"
          title="Add attachment"
          onClick={() => setAddModalOpen(true)}
        >
          <Icon
            iconKey="add"
            color={theme.foreground}
            width={18}
            height={18}
          />
        </button>

        <div className="activity-composer-input-wrap">
          <TextInput
            ref={inputRef}
            name="activity-comment"
            placeholder={replyingTo ? 'Write a reply…' : 'Write a comment…'}
            value={text}
            multiline
            expandable
            rows={1}
            hybrid
            aria-label="Comment"
            style={{
              minHeight: 40,
              maxHeight: 140,
              overflow: 'hidden',
              fontSize: 14,
              paddingRight: showSend ? 44 : 12,
              paddingTop: 12,
              resize: 'none',
            }}
            onChange={(event) => setText(event.target.value)}
            onFocus={() => setFocused(true)}
            onBlur={() => {
              if (!hasBody && !replyingTo) setFocused(false);
            }}
            onKeyDown={handleKeyDown}
          />
        </div>

        {showSend ? (
          <button
            className="activity-composer-send"
            type="button"
            disabled={sendDisabled}
            aria-label="Send comment"
            onClick={handleSubmit}
          >
            <Icon
              iconKey="send"
              width={18}
              height={18}
            />
          </button>
        ) : null}
      </div>

      {sending ? (
        <div className="activity-composer-upload-hint">Sending…</div>
      ) : null}

      <AddModal
        node={node}
        isOpen={addModalOpen}
        anchorNodeKey={node?.key || null}
        showInjectedAddTypes
        allowCreate={false}
        onClose={() => setAddModalOpen(false)}
        onExistingAttached={handleExistingAttached}
        onTypeSelect={handleQuickActionSelect}
      />

      <CameraModal
        isOpen={cameraOpen}
        setOpen={setCameraOpen}
        contentType={cameraContentType}
        onContentCreated={handleCameraContentCreated}
      />

      <NodeHiddenFileInputs
        photoInputRef={photoInputRef}
        videoInputRef={videoInputRef}
        documentInputRef={documentInputRef}
        filesInputRef={filesInputRef}
        onPhotoSelect={handleFilesFromInput}
        onVideoSelect={handleFilesFromInput}
        onDocumentSelect={handleFilesFromInput}
        onFilesSelect={handleFilesFromInput}
      />
    </div>
  );
});

ActivityComposer.displayName = 'ActivityComposer';

export default ActivityComposer;
